import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

// Lightweight shadcn-style checkbox built on a native <input type="checkbox">
// so we avoid pulling in @radix-ui/react-checkbox. Mirrors the shadcn API:
// pass `checked` + `onCheckedChange` and the component handles the visual
// state itself.
export interface CheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "checked" | "onChange"
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, defaultChecked, onCheckedChange, disabled, ...props }, ref) => {
    return (
      <label
        className={cn(
          "relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary ring-offset-background transition-colors",
          disabled && "cursor-not-allowed opacity-50",
          !disabled && "cursor-pointer",
          checked && "bg-primary text-primary-foreground",
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          {...props}
        />
        {checked ? (
          <Check className="pointer-events-none h-3.5 w-3.5" strokeWidth={3} />
        ) : null}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
